import type { Coordinate } from '../types';

import { MEDIA_PROXY_BASEURL, ASSET_URL_NON_PROXY_LIST } from '../constants';

/**
 * Calculates the distance between 2 points
 */
export function distance(point1: Coordinate, point2: Coordinate) {
    const xDistance = point1.x - point2.x;
    const yDistance = point1.y - point2.y;

    return Math.sqrt(xDistance * xDistance + yDistance * yDistance);
}

/**
 * Returns the median value
 */
export function median(numbers: Array<number>): number {
    // median of [3, 5, 4, 4, 1, 1, 2, 3] = 3
    let median = 0;
    const numsLen = numbers.length;

    numbers.sort();
    if (numsLen % 2 === 0) {
        median = (numbers[numsLen / 2 - 1] + numbers[numsLen / 2]) / 2;
    } else {
        median = numbers[(numsLen - 1) / 2];
    }

    return median;
}

/**
 * Clamp the given value between the min and max value
 */
export function clamp(value: number, minValue: number, maxValue: number): number {
    if (value < minValue) {
        return minValue;
    }
    if (value > maxValue) {
        return maxValue;
    }

    return value;
}

/**
 * Convert unicode to string
 */
export function unicodeToChar(text: string): string {
    return text.replace(/\\u[\dA-F]{4}/gi, function (match) {
        return String.fromCharCode(parseInt(match.replace(/\\u/g, ''), 16));
    });
}

function isOnNonProxyList(url: URL): boolean {
    if (url.protocol !== 'https:' && url.protocol !== 'http:') {
        return false;
    }
    return ASSET_URL_NON_PROXY_LIST.some((entry) => {
        const slash = entry.indexOf('/');
        const host = slash < 0 ? entry : entry.slice(0, slash);
        const pathPrefix = slash < 0 ? '/' : entry.slice(slash);
        const onHost = url.hostname === host || url.hostname.endsWith(`.${host}`);
        return onHost && url.pathname.startsWith(pathPrefix);
    });
}

function parseUrl(url: string): URL | null {
    try {
        return typeof window !== 'undefined' && window.location
            ? new URL(url, window.location.href)
            : new URL(url);
    } catch {
        return null;
    }
}

export function maybeProxy(url: string) {
    if (typeof url !== 'string') {
        return url;
    }

    if (url.toLowerCase().startsWith('data:')) {
        return url;
    }

    if (url.startsWith(MEDIA_PROXY_BASEURL)) {
        return url;
    }

    // ensure we don't proxy local assets
    if (url.toLowerCase().startsWith('base/test/resources/images')) {
        return url;
    }

    const parsed = parseUrl(url);
    if (!parsed) {
        return MEDIA_PROXY_BASEURL + encodeURIComponent(url);
    }

    // don't need CORS headers if we are on the same domain
    if (typeof window !== 'undefined' && parsed.hostname === window.location?.hostname) {
        return url;
    }

    if (isOnNonProxyList(parsed)) {
        return url;
    }

    return MEDIA_PROXY_BASEURL + encodeURIComponent(url);
}
